import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { ToolsService, OldCall } from 'src/app/api/tools.service';
import { TableSkeletonComponent } from 'src/app/ui/skeletons/table-skeleton/table-skeleton.component';
import { TableColumnComponent, TableHeaderComponent, Column } from 'src/app/ui/tables';
import { MessageService } from 'primeng/api';

@Component({
  selector: 'app-remove-old-calls-roots',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DialogModule,
    TableModule,
    ButtonModule,
    CardModule,
    TableSkeletonComponent,
    TableColumnComponent,
    TableHeaderComponent,
    DatePickerModule
  ],
  templateUrl: './remove-old-calls-roots.component.html',
  styleUrl: './remove-old-calls-roots.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RemoveOldCallsRootsComponent {

  maxDate = (() => {
    const date = new Date();
    date.setDate(date.getDate() - 90);
    return date;
  })();
  placeholderDate = this.maxDate.toISOString().split('T')[0];

  dateForm = new FormGroup({
    cutOffDate: new FormControl(this.placeholderDate, [Validators.required])
  });

  oldCalls = signal<OldCall[]>([]);
  isLoading = signal(false);
  hasSearched = signal(false);
  isApproving = signal(false);

  constructor(
    private toolsService: ToolsService,
    private messageService: MessageService
  ) { }

  callsColumns = signal<Column<OldCall>[]>([
    { field: 'partnerName', header: 'Partner Name', type: 'partnerNameWithLink', partnerIdField: 'partnerId', sortable: true, filterable: true },
    { field: 'staff', header: 'Staff', type: 'text', sortable: true, filterable: true },
    { field: 'logtype', header: 'Type', type: 'logtype', sortable: true, filterable: true },
    { field: 'callDate', header: 'Date Scheduled', type: 'date', sortable: true, filterable: true }
  ]);

  onConfirmDate() {
    if (this.dateForm.valid) {
      this.isLoading.set(true);
      let cutOffDate = this.dateForm.value.cutOffDate!;
      if (typeof cutOffDate === 'object' && cutOffDate !== null && 'toISOString' in cutOffDate) {
        cutOffDate = (cutOffDate as Date).toISOString().split('T')[0];
      }
      this.toolsService.getOldRootsCalls(cutOffDate).subscribe({
        next: (calls) => {
          this.oldCalls.set(calls);
          this.isLoading.set(false);
          this.hasSearched.set(true);
        },
        error: () => {
          this.isLoading.set(false);
        }
      });
    }
  }

  onApproveRemoval() {
    this.isApproving.set(true)
    let cutOffDate = this.dateForm.value.cutOffDate!;
    if ((cutOffDate as unknown) instanceof Date) {
      cutOffDate = ((cutOffDate as unknown) as Date).toISOString().split('T')[0];
    }
    this.toolsService.setInactiveOldRootsCalls(cutOffDate).subscribe({
      next: () => {
        this.oldCalls.set([]);
        this.isApproving.set(false);
        this.messageService.add({
          severity: 'success',
          summary: 'Removal Complete',
          detail: 'Old roots calls have been removed successfully.',
          life: 3000
        });
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Removal Failed',
          detail: err?.message || 'Failed to remove old roots calls. Please try again.',
          life: 5000
        });
      }
    });
  }
}